import React, { useEffect, useState } from "react";
import api, { reorderStatuses } from "../../lib/api";
import { CaretUp, CaretDown } from "@phosphor-icons/react";
import { TONE_CLASSES } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import SettingsSection from "./SettingsSection";

const BUCKETS = [
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "pending_client", label: "Pending Client" },
  { value: "completed", label: "Completed" },
  { value: "rejected", label: "Rejected" },
  { value: "none", label: "None" },
];

const TONES = Object.keys(TONE_CLASSES);

// Small inline swatch using the bg portion of a TONE_CLASSES string
function ToneSwatch({ tone }) {
  const cls = TONE_CLASSES[tone] || "";
  // Extract the bg class (first token)
  const bg = cls.split(" ").find((c) => c.startsWith("bg-")) || "bg-slate-100";
  const border = cls.split(" ").find((c) => c.startsWith("border-")) || "border-slate-200";
  return (
    <span
      className={`inline-block w-3 h-3 rounded-full border ${bg} ${border}`}
      title={tone}
    />
  );
}

const SELECT_CLS =
  "w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

export default function StatusesManager() {
  const [statuses, setStatuses] = useState([]);
  const [addLabel, setAddLabel] = useState("");
  const [addBucket, setAddBucket] = useState("open");
  const [addTone, setAddTone] = useState("slate");
  const [addClientSettable, setAddClientSettable] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const refresh = () =>
    api.get("/ticket-statuses").then((r) => setStatuses(r.data)).catch(() => {});

  useEffect(() => { refresh(); }, []);

  const patch = async (id, body) => {
    try {
      const { data } = await api.patch(`/ticket-statuses/${id}`, body);
      setStatuses((prev) => prev.map((s) => (s.id === id ? data : s)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  const add = async () => {
    if (!addLabel.trim()) { toast.error("Label is required"); return; }
    setSubmitting(true);
    try {
      await api.post("/ticket-statuses", {
        label: addLabel.trim(),
        bucket: addBucket,
        tone: addTone,
        client_settable: addClientSettable,
      });
      setAddLabel("");
      setAddBucket("open");
      setAddTone("slate");
      setAddClientSettable(false);
      refresh();
      toast.success("Status added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add status");
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (s) => {
    try {
      await api.delete(`/ticket-statuses/${s.id}`);
      setStatuses((prev) => prev.filter((x) => x.id !== s.id));
      toast.success("Status deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete status");
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= statuses.length) return;
    const next = statuses.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setStatuses(next);
    try {
      await reorderStatuses(next.map((s) => s.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reorder");
      refresh();
    }
  };

  return (
    <SettingsSection title="Ticket Statuses" description="Define the statuses tickets can move through, their bucket, colour, who can set them, which moves email the client, and which are client-only.">
      {/* Add form */}
      <div className="surface-card p-5 space-y-3">
        <div className="text-sm font-semibold text-slate-700">Add status</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Label</label>
            <input
              className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={addLabel}
              onChange={(e) => setAddLabel(e.target.value)}
              placeholder="e.g. Awaiting Review"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Bucket</label>
            <select className={SELECT_CLS} value={addBucket} onChange={(e) => setAddBucket(e.target.value)}>
              {BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Tone</label>
            <div className="flex items-center gap-2">
              <select className={SELECT_CLS} value={addTone} onChange={(e) => setAddTone(e.target.value)}>
                {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <ToneSwatch tone={addTone} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="add-client-settable"
            checked={addClientSettable}
            onChange={(e) => setAddClientSettable(e.target.checked)}
            className="rounded border-slate-300"
          />
          <label htmlFor="add-client-settable" className="text-sm text-slate-700">Client-settable</label>
        </div>
        <Button onClick={add} disabled={submitting}>
          {submitting ? "Adding…" : "Add Status"}
        </Button>
      </div>

      {/* Table */}
      <div className="surface-card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">
          Ticket statuses
        </div>
        <div className="divide-y divide-slate-100">
          {statuses.length === 0 && (
            <div className="px-4 py-6 text-xs text-slate-400 text-center">No statuses yet.</div>
          )}
          {statuses.map((s, idx) => (
            <div key={s.id} className="px-5 py-3.5 space-y-3">
              {/* Row 1: reorder, name, bucket, tone, delete/lock */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-col shrink-0">
                  <button
                    className="text-slate-300 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-300"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    aria-label="Move up"
                    title="Move up"
                  >
                    <CaretUp size={12} weight="bold" />
                  </button>
                  <button
                    className="text-slate-300 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-300"
                    onClick={() => move(idx, 1)}
                    disabled={idx === statuses.length - 1}
                    aria-label="Move down"
                    title="Move down"
                  >
                    <CaretDown size={12} weight="bold" />
                  </button>
                </div>

                <div className="min-w-[140px] flex-1">
                  <div className="text-sm font-medium text-slate-800">{s.label}</div>
                  <div className="text-xs text-slate-400">{s.key}</div>
                </div>

                <div className="w-36 shrink-0">
                  <select
                    className={SELECT_CLS}
                    value={s.bucket}
                    onChange={(e) => patch(s.id, { bucket: e.target.value })}
                  >
                    {BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </div>

                <div className="w-32 flex items-center gap-2 shrink-0">
                  <select
                    className={SELECT_CLS}
                    value={s.tone || "slate"}
                    onChange={(e) => patch(s.id, { tone: e.target.value })}
                  >
                    {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ToneSwatch tone={s.tone || "slate"} />
                </div>

                <div className="shrink-0 ml-auto sm:ml-0">
                  {s.system ? (
                    <span className="text-xs text-slate-400 border border-slate-200 rounded px-2 py-0.5">built-in</span>
                  ) : (
                    <button
                      className="text-xs text-rose-500 hover:text-rose-700"
                      onClick={() => del(s)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>

              {/* Row 2: behaviour toggles, grouped in a tidy strip */}
              <div className="pl-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Switch
                    checked={!!s.client_settable}
                    onCheckedChange={(v) => patch(s.id, { client_settable: v })}
                  />
                  <span className="text-slate-500">Client-settable</span>
                </label>

                <label className="flex items-center gap-1.5 cursor-pointer" title="Hide this status from staff — client-only workflow status">
                  <Switch
                    checked={!!s.hide_from_staff}
                    onCheckedChange={(v) => patch(s.id, { hide_from_staff: v })}
                  />
                  <span className="text-slate-500">Not for internal team</span>
                </label>

                <label className="flex items-center gap-1.5 cursor-pointer" title="Email the client when a ticket moves to this status">
                  <Switch
                    checked={s.notify_client != null ? !!s.notify_client : !!s.is_resolved}
                    onCheckedChange={(v) => patch(s.id, { notify_client: v })}
                  />
                  <span className="text-slate-500">Email client</span>
                </label>

                <label className="flex items-center gap-1.5 cursor-pointer ml-auto">
                  <Switch
                    checked={!!s.enabled}
                    onCheckedChange={(v) => patch(s.id, { enabled: v })}
                  />
                  <span className="text-slate-500">Enabled</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SettingsSection>
  );
}
